import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import Card from '../../../components/Card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import Loader from '../../../components/partials/components/loader';
import RenderDeleteModal from '../../../components/partials/components/delete-modal';
import { EditIcon, DeleteIcon } from '../../../components/setting/elements';
import { getApiCall, postApiCall } from '../../../service/httpCall';
import { API_AGENTS_LIST, API_NOTIFICATION_ASSIGN_TO_AGENT, API_NOTIFICATION_LIST, API_NOTIFICATION_DELETE, API_NOTIFICATION_UNASSIGN_FROM_AGENT } from '../../../service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '../../../utilities/constant';
import { useAuth, errorCheck } from '../auth/auth-utils';
import { getUser } from "../../../utilities/setting";

const NotificationList = () => {
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const [dataList, setDataList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [enableDeleteModal, setEnableDeleteModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isMultiDelete, setIsMultiDelete] = useState(false);
    const [isSuperAdmin, setIsSuperadmin] = useState(false);
    const [loggedInUserACL, setLoggedInUserACL] = useState({});

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [agentList, setAgentList] = useState([]);
    const [agentLoading, setAgentLoading] = useState(false);
    const [agentSearch, setAgentSearch] = useState("");
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [submittingAssign, setSubmittingAssign] = useState(false);
    const [submittingRemove, setSubmittingRemove] = useState(false);

    useEffect(() => {
        setLoading(true);
        const user = getUser();
            if (user.role === USER_ROLES.SUPERADMIN || user.role === USER_ROLES.SUB_SUPERADMIN) setIsSuperadmin(true);
            if (user?.acl_settings) setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
        fetchData();
    }, []);

    const fetchData = () => {
        getApiCall(API_NOTIFICATION_LIST)
            .then((resp) => {
                const data = resp?.data || resp;
                const updated = Array.isArray(data) ? data.map((val, idx) => ({ ...val, key: idx + 1 })) : [];
                setDataList(updated);
                setOriginalData(updated);
            })
            .catch((err) => {
                toast.error(JSON.stringify(err) || "Failed to fetch notifications");
                console.log("Error fetching notifications:", err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (searchValue.trim()) {
            const filtered = originalData.filter(item =>
                item.notification_type?.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchValue.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalData);
        }
    }, [searchValue, originalData]);

    useEffect(() => {
        if (confirmDelete) {
            if (isMultiDelete && Array.isArray(selectedItems)) {
                const deletePromises = selectedItems.map(id =>
                    postApiCall(API_NOTIFICATION_DELETE, { id: String(id) })
                );

                Promise.all(deletePromises)
                    .then(() => {
                        toast.success("Selected notifications deleted");
                        fetchData();
                        setSelectedItems([]);
                    })
                    .catch((err) => {
                        toast.error(JSON.stringify(err) || "Failed to delete selected notifications");
                        console.log("Error deleting notifications:", err);
                    })
                    .finally(() => {
                        setConfirmDelete(false);
                        setEnableDeleteModal(false);
                    });
            } else {
                postApiCall(API_NOTIFICATION_DELETE, { id: String(deleteId) })
                    .then(() => {
                        toast.success("Notification deleted successfully");
                        fetchData();
                    })
                    .catch((err) => {
                        toast.error(JSON.stringify(err) || "Failed to delete notification");
                        console.log("Error deleting notification:", err);
                    })
                    .finally(() => {
                        setConfirmDelete(false);
                        setEnableDeleteModal(false);
                    });
            }
        }
    }, [confirmDelete]);

    const canManageNotifications = isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.edit;

    const handleSelectAll = (e) => {
        setSelectedItems(e.checked ? dataList.map(item => item.id) : []);
    };

    const loadAgents = async () => {
        if (agentLoading || agentList.length > 0) return;
        try {
            setAgentLoading(true);
            const resp = await getApiCall(API_AGENTS_LIST);
            const normalized = Array.isArray(resp) ? resp.map((a) => ({
                id: a.id,
                agent_name: a.agent_name || a.username || `Agent #${a.id}`,
                username: a.username || "",
                agent_number: a.agent_number || "",
            })) : [];
            setAgentList(normalized);
        } catch (e) {
            toast.error(e || "Failed to load agents");
        } finally {
            setAgentLoading(false);
        }
    };

    const openAssignModal = async () => {
        if (selectedItems.length === 0) return toast.warning("Select at least one notification");
        await loadAgents();
        setSelectedAgentIds([]);
        setAgentSearch("");
        setShowAssignModal(true);
    };

    const openRemoveModal = async () => {
        if (selectedItems.length === 0) return toast.warning("Select at least one notification");
        await loadAgents();
        setSelectedAgentIds([]);
        setAgentSearch("");
        setShowRemoveModal(true);
    };

    const submitAssignRemove = async (mode) => {
        const isAssign = mode === "assign";
        if (selectedItems.length === 0) return toast.warning("Select at least one notification");
        if (selectedAgentIds.length === 0) return toast.warning("Select at least one agent");

        const payload = {
            notification_ids: selectedItems.map((id) => String(id)),
            agent_ids: selectedAgentIds.map((id) => String(id)),
        };

        try {
            isAssign ? setSubmittingAssign(true) : setSubmittingRemove(true);
            await postApiCall(isAssign ? API_NOTIFICATION_ASSIGN_TO_AGENT : API_NOTIFICATION_UNASSIGN_FROM_AGENT, payload);
            toast.success(isAssign ? "Assigned successfully" : "Removed successfully");
            setShowAssignModal(false);
            setShowRemoveModal(false);
            setSelectedAgentIds([]);
        } catch (e) {
            toast.error(e || "Something went wrong");
        } finally {
            isAssign ? setSubmittingAssign(false) : setSubmittingRemove(false);
        }
    };

    const selectedNotificationsPreview = () => {
        const selectedSet = new Set(selectedItems);
        const items = originalData.filter((n) => selectedSet.has(n.id)).slice(0, 10);
        return (
            <div className="small text-muted">
                Selected: {selectedItems.length}
                {items.length > 0 && (
                    <ul className="mt-2 mb-0">
                        {items.map((n) => (
                            <li key={n.id}>
                                {n.notification_type}{n.description ? ` - ${n.description}` : ""}
                            </li>
                        ))}
                        {selectedItems.length > 10 && <li>...and more</li>}
                    </ul>
                )}
            </div>
        );
    };

    const filteredAgents = agentSearch.trim()
        ? agentList.filter((a) =>
            `${a.agent_name} ${a.username} ${a.agent_number}`.toLowerCase().includes(agentSearch.toLowerCase())
        )
        : agentList;

    const toggleAgent = (id) => {
        setSelectedAgentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleSelectAllAgents = (e) => {
        setSelectedAgentIds(e.checked ? filteredAgents.map((a) => a.id) : []);
    };

    const actionTemplate = (rowData) => (
        <div className="d-flex align-items-center list-user-action">
            {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.edit) && (
                <EditIcon linkTo={`/notifications/edit/${rowData.id}`} />
            )}
            <div style={{ marginLeft: "10px" }}>
                {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.delete) && selectedItems.length === 0 && (
                    <DeleteIcon handleCallback={() => {
                        setDeleteId(rowData.id);
                        setIsMultiDelete(false);
                        setEnableDeleteModal(true);
                    }} />
                )}
            </div>
        </div>
    );

    if (loading) return <Loader />;

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <h4 className="card-title">Notification List</h4>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" onClick={() => navigate(`/${ROUTES.NOTIFICATION.ASSIGNMENTS}`)}>
                                    Assigned Notifications
                                </Button>
                                {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.add) && (
                                <Button onClick={() => navigate(`/${ROUTES.NOTIFICATION.CREATE}`)}>Create Notification</Button>
                            )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex mb-3">
                                <Button 
                                    variant="danger" 
                                    className="me-2" 
                                    disabled={selectedItems.length === 0} 
                                    onClick={() => { setIsMultiDelete(true); setEnableDeleteModal(true); }}
                                >
                                    Delete Selected
                                </Button>
                                {canManageNotifications && (
                                    <>
                                        <Button
                                            variant="primary"
                                            className="me-2"
                                            disabled={selectedItems.length === 0}
                                            onClick={openAssignModal}
                                        >
                                            Assign to Agent(s)
                                        </Button>
                                        <Button
                                            variant="warning"
                                            className="me-2"
                                            disabled={selectedItems.length === 0}
                                            onClick={openRemoveModal}
                                        >
                                            Remove from Agent(s)
                                        </Button>
                                    </>
                                )}
                                <Form.Control
                                    className="w-25"
                                    placeholder="Search notifications..."
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                            <DataTable value={dataList} paginator rows={10}>
                                <Column 
                                    header={<Checkbox checked={selectedItems.length === dataList.length && dataList.length > 0} onChange={handleSelectAll} />}
                                    body={(rd) => (
                                        <Checkbox 
                                            checked={selectedItems.includes(rd.id)} 
                                            onChange={() => setSelectedItems(prev => prev.includes(rd.id) ? prev.filter(i => i !== rd.id) : [...prev, rd.id])} 
                                        />
                                    )} 
                                />
                                <Column field="notification_type" header="Title" sortable />
                                <Column field="description" header="Description" style={{ maxWidth: "350px" }} />
                                {/* <Column 
                                    field="status" 
                                    header="Status" 
                                    body={(rd) => (
                                        <span className={`badge ${rd.status == 1 ? 'bg-success' : 'bg-danger'}`}>
                                            {rd.status == 1 ? "Active" : "Inactive"}
                                        </span>
                                    )} 
                                /> */}
                                <Column 
                                 field="is_general" 
                                 header="Is General" 
                                 sortable
                                 body={(rd) => (
                                 <span className={`badge ${rd.is_general ? 'bg-info' : 'bg-secondary'}`}>
                                 {rd.is_general ? "Yes" : "No"}
                             </span>
    )} 
/>
                                <Column 
                                    field="created_at" 
                                    header="Created Date" 
                                    body={(rowData) => new Date(rowData.created_at).toLocaleString("en-IN")}
                                />
                                <Column header="Action" body={actionTemplate} />
                            </DataTable>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <RenderDeleteModal 
                show={enableDeleteModal} 
                setShow={setEnableDeleteModal} 
                setConfirmDelete={setConfirmDelete} 
                isMultiDelete={isMultiDelete} 
            />

            {/* Assign Modal */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Assign Notifications to Agent(s)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedNotificationsPreview()}
                    <Form.Group className="mt-3">
                        <Form.Label>Search Agents</Form.Label>
                        <Form.Control
                            placeholder="Search by name / username / number"
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                        />
                    </Form.Group>
                    <div className="mt-3" style={{ maxHeight: 320, overflow: "auto", border: "1px solid #eee", borderRadius: 6, padding: 10 }}>
                        <div className="d-flex align-items-center mb-2">
                            <Checkbox
                                checked={filteredAgents.length > 0 && selectedAgentIds.length === filteredAgents.length}
                                onChange={handleSelectAllAgents}
                            />
                            <span className="ms-2">Select all agents</span>
                        </div>
                        {agentLoading ? (
                            <div className="text-muted">Loading agents...</div>
                        ) : filteredAgents.length === 0 ? (
                            <div className="text-muted">No agents found</div>
                        ) : (
                            filteredAgents.map((a) => (
                                <div key={a.id} className="d-flex align-items-center py-1">
                                    <Checkbox checked={selectedAgentIds.includes(a.id)} onChange={() => toggleAgent(a.id)} />
                                    <div className="ms-2">
                                        <div>{a.agent_name}</div>
                                        <div className="small text-muted">{[a.username, a.agent_number].filter(Boolean).join(" - ")}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={() => submitAssignRemove("assign")} disabled={submittingAssign || agentLoading}>
                        {submittingAssign ? "Assigning..." : "Assign"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Remove Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Notifications from Agent(s)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedNotificationsPreview()}
                    <Form.Group className="mt-3">
                        <Form.Label>Search Agents</Form.Label>
                        <Form.Control
                            placeholder="Search by name / username / number"
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                        />
                    </Form.Group>
                    <div className="mt-3" style={{ maxHeight: 320, overflow: "auto", border: "1px solid #eee", borderRadius: 6, padding: 10 }}>
                        <div className="d-flex align-items-center mb-2">
                            <Checkbox
                                checked={filteredAgents.length > 0 && selectedAgentIds.length === filteredAgents.length}
                                onChange={handleSelectAllAgents}
                            />
                            <span className="ms-2">Select all agents</span>
                        </div>
                        {agentLoading ? (
                            <div className="text-muted">Loading agents...</div>
                        ) : filteredAgents.length === 0 ? (
                            <div className="text-muted">No agents found</div>
                        ) : (
                            filteredAgents.map((a) => (
                                <div key={a.id} className="d-flex align-items-center py-1">
                                    <Checkbox checked={selectedAgentIds.includes(a.id)} onChange={() => toggleAgent(a.id)} />
                                    <div className="ms-2">
                                        <div>{a.agent_name}</div>
                                        <div className="small text-muted">{[a.username, a.agent_number].filter(Boolean).join(" - ")}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>Cancel</Button>
                    <Button variant="warning" onClick={() => submitAssignRemove("remove")} disabled={submittingRemove || agentLoading}>
                        {submittingRemove ? "Removing..." : "Remove"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NotificationList;
